import axios, { AxiosError, AxiosResponse } from "axios";
import { getValidToken } from "../utils/authUtils";

// Types
export interface ApiResponse<T> {
  isSuccess: boolean;
  data: T;
  message?: string;
}

export interface Driver {
  id: string;
  name: string;
  contactNumber: string;
  aadharNumber: string;
  drivingLicense: string;
  employmentType: string;
  address: string;
  status: string;
  joiningDate: string;
  salary: number;
}

export interface Cleaner {
  id: string;
  name: string;
  contactNumber: string;
  aadharNumber: string;
  employmentType: string;
  address: string;
  status: string;
  joiningDate: string;
  salary: number;
}

export interface Lady {
  id: string;
  name: string;
  contactNumber: string;
  aadharNumber: string;
  employmentType: string;
  address: string;
  status: string;
  joiningDate: string;
  salary: number;
}

export interface Manager {
  id: string;
  name: string;
  contactNumber: string;
  aadharNumber: string;
  employmentType: string;
  address: string;
  status: string;
  joiningDate: string;
  salary: number;
}

export interface PetrolPump {
  id: string;
  petrolPumpName: string;
  primaryContactName: string;
  primaryContactNumber: string;
  providerName: string;
  location: string;
  bulkDiscountPerLitre: number;
}

export interface Parking {
  id: string;
  name: string;
  contactNumber: string;
  location: string;
  // ...other fields as needed
}

export interface Mechanic {
  id: string;
  name: string;
  phoneNumber: string;
  location: string;
  idType?: string;
  idNumber?: string;
  // ...other fields as needed
}

export interface ServiceOperator {
  id: string;
  name: string;
  type: string;
  operationType: string;
  contactPerson: string;
  contactNumber: string;
  pickupLocation?: string;
  pricingType?: string;
  pricingValue?: number;
  gstPercentage?: number;
  // ...other fields as needed
}

export interface PickupMan {
  id: string;
  name: string;
  contactNumber: string;
  location: string;
  commissionType: string;
  commissionValue: number;
  status: string;
}

export interface Garage {
  id: string;
  name: string;
  contactNumber: string;
  location: string;
  contactName: string;
}

export interface Vehicle {
  id: string;
  status: string;
  vehicleType: string;
  acquisitionType: string;
  vehicleNumber: string;
  chassisNumber: string;
  engineNumber: string;
  makeYear: string;
  model: string;
  color: string;
  bodyType: string;
  configuration: string;
  fuelType: string;
  seatingCapacity: string;
  isAC: boolean;
}

export interface Stop {
  id: string;
  stopName: string;
  routeId: string;
  contactPersonName: string;
  contactPersonNumber: string;
}

export interface Route {
  id: string;
  name: string;
  fromLocation: string;
  toLocation: string;
  routeManagerName: string;
  routeManagerNumber: string;
  stops: Stop[];
  schedules?: any[];
}

// Create axios instance with default config
const api = axios.create({
  baseURL: process.env.REACT_APP_API_HOST,
});

// Add request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    const token = await getValidToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle common errors
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      window.location.href = "/";
    }
    return Promise.reject(error);
  }
);

// API endpoints
export const endpoints = {
  staff: {
    search: (type: string, search: string) =>
      `/staff-members?type=${type}&search=${search}`,
  },
};

// API methods with loading state handling
export const staffApi = {
  searchDrivers: async (searchTerm: string): Promise<Driver[]> => {
    try {
      const response: AxiosResponse<ApiResponse<Driver[]>> = await api.get(
        endpoints.staff.search("DRIVER", searchTerm)
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to search drivers"
        );
      }
      throw error;
    }
  },
  searchCleaners: async (searchTerm: string): Promise<Driver[]> => {
    try {
      const response: AxiosResponse<ApiResponse<Driver[]>> = await api.get(
        endpoints.staff.search("CLEANER", searchTerm)
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to search cleaners"
        );
      }
      throw error;
    }
  },
  searchLadies: async (searchTerm: string): Promise<Lady[]> => {
    try {
      const response: AxiosResponse<ApiResponse<Lady[]>> = await api.get(
        endpoints.staff.search("LADY", searchTerm)
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to search ladies"
        );
      }
      throw error;
    }
  },
  searchManagers: async (searchTerm: string): Promise<Manager[]> => {
    try {
      const response: AxiosResponse<ApiResponse<Manager[]>> = await api.get(
        endpoints.staff.search("MANAGER", searchTerm)
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to search managers"
        );
      }
      throw error;
    }
  },
};

export const petrolPumpApi = {
  search: async (searchTerm: string): Promise<PetrolPump[]> => {
    try {
      const response: AxiosResponse<ApiResponse<PetrolPump[]>> = await api.get(
        `/master-data/petrol-pumps?search=${encodeURIComponent(searchTerm)}`
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to search petrol pumps"
        );
      }
      throw error;
    }
  },
};

export const parkingApi = {
  search: async (searchTerm: string): Promise<Parking[]> => {
    try {
      const response: AxiosResponse<ApiResponse<Parking[]>> = await api.get(
        `/master-data/parkings?search=${encodeURIComponent(searchTerm)}`
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to search parkings"
        );
      }
      throw error;
    }
  },
};

export const mechanicApi = {
  search: async (searchTerm: string): Promise<Mechanic[]> => {
    try {
      const response: AxiosResponse<ApiResponse<Mechanic[]>> = await api.get(
        `/master-data/mechanics?search=${encodeURIComponent(searchTerm)}`
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to search mechanics"
        );
      }
      throw error;
    }
  },
};

export const serviceOperatorApi = {
  search: async (searchTerm: string, filters?: any): Promise<ServiceOperator[]> => {
    try {
      const queryParams = new URLSearchParams({
        limit: "100",
        ...(filters && { ...filters }),
        ...(searchTerm && { search: searchTerm }),
      });
      const response: AxiosResponse<ApiResponse<ServiceOperator[]>> =
        await api.get(`/service-operators?${queryParams}`);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to search service operators"
        );
      }
      throw error;
    }
  },
  getAllAgents: async (): Promise<ServiceOperator[]> => {
    try {
      const response: AxiosResponse<ApiResponse<ServiceOperator[]>> =
        await api.get(`/service-operators/agent`);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to search agents"
        );
      }
      throw error;
    }
  },
  searchAgentBranch: async (location: string): Promise<ServiceOperator[]> => {
    try {
      const response: AxiosResponse<ApiResponse<ServiceOperator[]>> =
        await api.get(`/service-operators/agent/location?location=${location}`);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to search agent branch"
        );
      }
      throw error;
    }
  },
};
export const pickupManApi = {
  search: async (searchTerm: string): Promise<PickupMan[]> => {
    try {
      const response: AxiosResponse<ApiResponse<PickupMan[]>> = await api.get(
        `/master-data/pickup-man?limit=100`
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to search service operators"
        );
      }
      throw error;
    }
  },
};
export const garageApi = {
  search: async (searchTerm: string): Promise<Garage[]> => {
    try {
      const response: AxiosResponse<ApiResponse<Garage[]>> = await api.get(
        `/master-data/garages?search=${encodeURIComponent(searchTerm)}`
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to search garages"
        );
      }
      throw error;
    }
  },
};

// mechanics api
export const mechanicsApi = {
  search: async (searchTerm: string): Promise<Mechanic[]> => {
    try {
      const response: AxiosResponse<ApiResponse<Mechanic[]>> = await api.get(
        `/master-data/mechanics?search=${encodeURIComponent(searchTerm)}`
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to search mechanics"
        );
      }
      throw error;
    }
  },
};

export const vehicleApi = {
  search: async (searchTerm: string): Promise<Vehicle[]> => {
    try {
      const queryParams = new URLSearchParams({
        page: "1",
        limit: "100",
        status: "Available",
      });
      const response: AxiosResponse<ApiResponse<Vehicle[]>> = await api.get(
        `vehicles/v2/get?${queryParams}&search=${encodeURIComponent(
          searchTerm
        )}`
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to search vehicles"
        );
      }
      throw error;
    }
  },
};

export const ownerApi = {
  getOutsourcedOwners: async (searchTerm: string): Promise<Vehicle[]> => {
    try {
      const response: AxiosResponse<ApiResponse<Vehicle[]>> = await api.get(
        `vehicles/outsourced/owners?search=${encodeURIComponent(searchTerm)}`
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to search owners"
        );
      }
      throw error;
    }
  },
};

export const routeApi = {
  search: async (searchTerm: string): Promise<Route[]> => {
    try {
      const response: AxiosResponse<ApiResponse<Route[]>> = await api.get(
        `/routes/company`
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to search routes"
        );
      }
      throw error;
    }
  },
};
export default api;
